import { useState, useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { useSelect } from 'downshift';
import useSlingApi from '@sdk/api/useSlingApi';
import Column from '@sdk/components/ui/spacing/Column';
import EllipsisStyle from '@sdk/components/ui/EllipsisStyle';
import Text14 from '@sdk/components/ui/typography/Text14';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import Spinner from '@sdk/components/ui/Spinner';
import Chevron, { ChevronDirection } from '@sdk/components/ui/Chevron';
import defaultCurrencies from '@sdk/common/currency/currencies.json';
import gettext from '@sdk/lang/gettext';
import { useCurrency } from '@sdk/common/currency/CurrencyContext';
import { findCurrencyByCode } from '@sdk/common/currency/helpers/findCurrencyByCode';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { INPUT_HEIGHT } from '@sdk/components/forms/constants';
import { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import SlingFormFieldStyle, {
  focusedStyle,
} from '@sdk/components/forms/components/SlingFormFieldStyle';
import { FontWeightsType } from '@sdk/theme/FontWeightsType';
import type { RenderLabel } from '@sdk/components/forms/types';

const PickerContainer = styled(Column)<{ customWidth?: number }>`
  flex: 1;
  position: relative;
  ${({ customWidth }): string =>
    customWidth && `max-width: ${customWidth}px; min-width: ${customWidth}px`}
`;

const PickerToggle = styled.button<{ height: number }>`
  ${SlingFormFieldStyle}
  height: ${({ height }): number => height}px;

  position: relative;
  cursor: pointer;
  justify-content: space-between;

  &:focus {
    ${focusedStyle}

    .picker-toggle-text {
      color: ${(props): string => props.theme.colors.primary};
    }
  }

  &:disabled {
    background-color: ${(props): string => props.theme.colors.separatorOnGray};
    cursor: not-allowed;
  }
`;

export const PickerMenu = styled(Column)<{ width?: number }>`
  position: absolute;
  z-index: 1;
  min-width: 100px;
  max-height: 300px;
  overflow-y: auto;
  border-radius: 4px;
  background-color: ${(props): string => props.theme.colors.white};
  top: 100%;
  box-shadow: 0 10px 40px 0 ${({ theme }): string => theme.colors.boxShadowDarker};
  width: ${({ width }): string => (width ? `${width}px` : `100%`)};
  list-style: none;
  outline: none;
`;

const PickerItem = styled.li<{ highlighted?: boolean }>`
  cursor: pointer;
  padding: 10px;
  margin: 0 10px;
  border-radius: 4px;
  background-color: ${({ highlighted, theme }): string =>
    highlighted ? theme.colors.hoverBrand : theme.colors.white};

  color: ${({ highlighted, theme }): string =>
    highlighted ? theme.colors.primary : theme.colors.gray};

  &:first-child {
    margin-top: 10px;
  }
  &:last-child {
    margin-bottom: 10px;
  }
  &:hover {
    background-color: ${({ theme }): string => theme.colors.hoverBrand};
  }
`;

const PickerTextContainer = styled.div`
  ${EllipsisStyle}
`;

const PickerItemText = styled(Text14)<{ highlighted?: boolean }>`
  color: ${({ highlighted, theme }): string =>
    highlighted ? theme.colors.primary : theme.colors.gray};
`;

const Separator = styled(HorizontalSeparator)`
  margin: 5px 10px 5px 10px;
`;

const BottomItem = styled(Column)<{ highlighted?: boolean }>`
  padding: 10px;
  margin: 0 10px;
  border-radius: 4px;
  cursor: pointer;

  background-color: ${({ highlighted, theme }): string =>
    highlighted ? theme.colors.hoverBrand : theme.colors.white};
`;

export type Props = {
  currency: string;
  renderLabel?: RenderLabel;
  height?: number;
  menuWidth?: number;
  disabled?: boolean;
  invalid?: boolean;
  customWidth?: number;
  fontWeight?: string;
  onChange: (currency: CurrencyType) => void;
  onFocusChange?: (focused: boolean) => void;
};

const CurrencyPicker: React.FC<React.PropsWithChildren<Props>> = ({
  currency: currencyCode,
  disabled,
  invalid,
  onChange,
  renderLabel,
  onFocusChange,
  menuWidth,
  customWidth,
  height = INPUT_HEIGHT,
  fontWeight,
}) => {
  const api = useSlingApi();
  const [focused, setFocused] = useState(false);
  const [allCurrencies, setAllCurrencies] = useState<CurrencyType[]>(null);
  const [currencies, setCurrencies] = useState<CurrencyType[]>(defaultCurrencies as CurrencyType[]);
  const [showLoadMoreBtn, setShowLoadMoreBtn] = useState(true);
  const [isGettingData, setIsGettingData] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const { defaultCurrency } = useCurrency();

  const bottomItemIndex = currencies.length;

  const prepareCurrenciesList = (
    currencies: CurrencyType[],
    selectedCurrency: CurrencyType
  ): CurrencyType[] => {
    const selected = findCurrencyByCode(currencies, selectedCurrency.code);
    if (!selected) {
      return [...currencies, selectedCurrency];
    }

    return currencies;
  };

  useEffect(() => {
    setSelectedCurrency(findCurrencyByCode(currencies, currencyCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyCode]);

  useEffect(() => {
    (async (): Promise<void> => {
      let selectedCurrency;
      selectedCurrency = findCurrencyByCode(currencies, currencyCode);

      if (!selectedCurrency) {
        const { data: allCurrencies } = await api.laborCost.getAllCurrencies();
        selectedCurrency = findCurrencyByCode(allCurrencies, currencyCode);
        setAllCurrencies(allCurrencies);
        setCurrencies(prepareCurrenciesList(defaultCurrencies as CurrencyType[], selectedCurrency));
        setShowLoadMoreBtn(true);

        if (!selectedCurrency) {
          selectedCurrency = defaultCurrency;
        }
      }

      setSelectedCurrency(selectedCurrency);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onFocusChange?.(focused);
    if (selectedCurrency) {
      setCurrencies(prepareCurrenciesList(defaultCurrencies as CurrencyType[], selectedCurrency));
      setShowLoadMoreBtn(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused, onFocusChange]);

  const onChangeCallback = (selectedOption: string): void => {
    const currency = findCurrencyByCode(currencies, selectedOption);
    setSelectedCurrency(currency);
    onChange(currency);
  };

  const { isOpen, getToggleButtonProps, getMenuProps, highlightedIndex, getItemProps, openMenu } =
    useSelect({
      items: currencies.map(({ code }) => code),
      onSelectedItemChange: ({ selectedItem }) => {
        onChangeCallback(selectedItem);
      },
    });

  const showAllCurrencies = async (): Promise<void> => {
    openMenu();
    setShowLoadMoreBtn(false);

    if (allCurrencies) {
      setCurrencies(allCurrencies);
      return;
    }

    setIsGettingData(true);
    const { data } = await api.laborCost.getAllCurrencies();

    setCurrencies(data);
    setAllCurrencies(data);
    setIsGettingData(false);
  };

  const renderSpinner = (): JSX.Element => {
    return (
      <Column pTop={20} pBottom={20}>
        <Spinner />
      </Column>
    );
  };

  const renderPickerMenuContent = (): JSX.Element => {
    if (isGettingData) {
      return renderSpinner();
    }
    if (!isGettingData) {
      return (
        <>
          {currencies.map(({ code, name }, index) => (
            <PickerItem
              key={code}
              highlighted={highlightedIndex === index}
              {...getItemProps({ item: name, index })}>
              <PickerTextContainer>
                <PickerItemText
                  fontWeight={fontWeight || FontWeightsType.normal}
                  highlighted={
                    highlightedIndex === index || selectedCurrency.code === code
                  }>{`${code} - ${name}`}</PickerItemText>
              </PickerTextContainer>
            </PickerItem>
          ))}
          {showLoadMoreBtn && !isGettingData ? (
            <Column>
              <Separator />
              <Column pBottom={5}>
                <BottomItem
                  onClick={(): Promise<void> => showAllCurrencies()}
                  highlighted={highlightedIndex === bottomItemIndex}>
                  <PickerTextContainer
                    {...getItemProps({ item: 'bottomButtonItem', index: bottomItemIndex })}>
                    <PickerItemText
                      highlighted={highlightedIndex === bottomItemIndex}
                      fontWeight={fontWeight || FontWeightsType.normal}>
                      {gettext('Show all currencies')}
                    </PickerItemText>
                  </PickerTextContainer>
                </BottomItem>
              </Column>
            </Column>
          ) : null}
        </>
      );
    }
  };

  return (
    <PickerContainer customWidth={customWidth}>
      {renderLabel && renderLabel({ focused, invalid })}
      <PickerToggle
        {...getToggleButtonProps({ disabled })}
        type="button"
        invalid={invalid}
        onFocus={(): void => setFocused(true)}
        onBlur={(): void => setFocused(false)}
        height={height}>
        <PickerTextContainer>
          <Text14
            fontWeight={fontWeight || FontWeightsType.normal}
            themeColor={ThemeColorType.gray}>
            {selectedCurrency?.name}
          </Text14>
        </PickerTextContainer>
        <Chevron chevronDirection={isOpen ? ChevronDirection.up : ChevronDirection.down} />
      </PickerToggle>
      <PickerMenu {...getMenuProps()} width={menuWidth}>
        {isOpen ? renderPickerMenuContent() : null}
      </PickerMenu>
    </PickerContainer>
  );
};

export default CurrencyPicker;
